// src/pages/Dashboard/Admin/DepensesRecettesDashboard/utils/accountingDashboardUtils.js

export const EXPORT_COLUMNS = [
  "id",
  "saison",
  "date_operation",
  "designation",
  "categorie",
  "beneficiaire",
  "montant",
  "type_operation",
  "type_reglement",
  "plan_comptable_id",
  "plan_comptable_code",
  "lien_url",
  "photo_url",
];

export function safeArray(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
}

export function toNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

export function fmtEUR(value, maximumFractionDigits = 2) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits,
  }).format(toNumber(value));
}

export function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

export function formatDateFR(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function getActiveSeasonFromSettings(settings) {
  if (!settings) return "";
  if (Array.isArray(settings)) return settings?.[0]?.saison_active || "";
  return settings?.saison_active || "";
}

export function getSaisonsFromOperations(rows) {
  return Array.from(
    new Set(safeArray(rows).map((row) => row?.saison).filter(Boolean))
  ).sort((a, b) => String(b).localeCompare(String(a), "fr"));
}

export function getCategoriesFromOperations(rows) {
  return Array.from(
    new Set(safeArray(rows).map((row) => row?.categorie).filter(Boolean))
  ).sort((a, b) => String(a).localeCompare(String(b), "fr"));
}

export function filterOperations(rows, filters) {
  const query = normalizeText(filters?.q || "");
  const tokens = query.split(/\s+/).filter(Boolean);

  return safeArray(rows).filter((operation) => {
    const searchable = normalizeText(
      [
        operation?.designation,
        operation?.beneficiaire,
        operation?.categorie,
        operation?.type_operation,
        operation?.type_reglement,
        operation?.montant,
        operation?.saison,
      ]
        .filter(Boolean)
        .join(" ")
    );

    const matchQ =
      !tokens.length || tokens.every((token) => searchable.includes(token));

    const matchSaison =
      !filters?.saison || operation?.saison === filters.saison;

    const matchCategorie =
      !filters?.categorie || operation?.categorie === filters.categorie;

    const matchType =
      !filters?.type || operation?.type_operation === filters.type;

    const matchPlan =
      !filters?.planComptableId ||
      String(operation?.plan_comptable_id || "") ===
        String(filters.planComptableId);

    return matchQ && matchSaison && matchCategorie && matchType && matchPlan;
  });
}

export function calculateAccountingTotals(rows) {
  const depense = safeArray(rows)
    .filter((row) => row?.type_operation === "Dépense")
    .reduce((sum, row) => sum + toNumber(row?.montant), 0);

  const recette = safeArray(rows)
    .filter((row) => row?.type_operation === "Recette")
    .reduce((sum, row) => sum + toNumber(row?.montant), 0);

  return {
    depense,
    recette,
    solde: recette - depense,
  };
}

export function calculateAccountingStats(rows, planComptables = []) {
  const operations = safeArray(rows);
  const totals = calculateAccountingTotals(operations);

  const withoutPlan = operations.filter((row) => !row?.plan_comptable_id).length;
  const withDocument = operations.filter(
    (row) => row?.photo_url || row?.lien_url
  ).length;

  const categories = new Set(
    operations.map((row) => row?.categorie).filter(Boolean)
  );

  const planIds = new Set(
    operations.map((row) => row?.plan_comptable_id).filter(Boolean)
  );

  const topDepense = operations
    .filter((row) => row?.type_operation === "Dépense")
    .sort((a, b) => toNumber(b?.montant) - toNumber(a?.montant))[0];

  const topRecette = operations
    .filter((row) => row?.type_operation === "Recette")
    .sort((a, b) => toNumber(b?.montant) - toNumber(a?.montant))[0];

  return {
    ...totals,
    count: operations.length,
    depenseCount: operations.filter((row) => row?.type_operation === "Dépense")
      .length,
    recetteCount: operations.filter((row) => row?.type_operation === "Recette")
      .length,
    categoriesCount: categories.size,
    plansCount: planIds.size,
    withoutPlan,
    withDocument,
    topDepense,
    topRecette,
    coverageRate: operations.length
      ? Math.round((withDocument / operations.length) * 100)
      : 0,
    missingPlanRate: operations.length
      ? Math.round((withoutPlan / operations.length) * 100)
      : 0,
    totalPlansAvailable: safeArray(planComptables).length,
  };
}

export function getPlanById(planComptables, id) {
  return safeArray(planComptables).find(
    (plan) => String(plan?.id) === String(id)
  );
}

export function getPlanLabel(planComptables, id) {
  const plan = getPlanById(planComptables, id);
  return plan ? `${plan.code} — ${plan.intitule}` : "";
}

export function seasonBounds(season) {
  const [startYear, endYear] = String(season || "").split("-");

  return {
    start: new Date(`${startYear}-07-01T00:00:00`),
    end: new Date(`${endYear}-06-30T23:59:59`),
  };
}

export function isAcompteText(value = "") {
  return /acompte|arrhes|d[ée]p[ôo]t|deposit|advance/i.test(
    String(value || "")
  );
}

export function asExcelDateOrEmpty(value) {
  if (!value) return undefined;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  if (date.getFullYear() < 1901) return undefined;

  return date;
}

export function buildExportRows(rows, planComptables) {
  return safeArray(rows).map((operation) => {
    const plan = getPlanById(planComptables, operation?.plan_comptable_id);

    return {
      id: operation?.id ?? "",
      saison: operation?.saison ?? "",
      date_operation: asExcelDateOrEmpty(operation?.date_operation),
      designation: operation?.designation ?? "",
      categorie: operation?.categorie ?? "",
      beneficiaire: operation?.beneficiaire ?? "",
      montant: Number.isFinite(Number(operation?.montant))
        ? Number(operation?.montant)
        : "",
      type_operation: operation?.type_operation ?? "",
      type_reglement: operation?.type_reglement ?? "",
      plan_comptable_id: operation?.plan_comptable_id ?? "",
      plan_comptable_code: plan?.code ?? "",
      lien_url: operation?.lien_url ?? "",
      photo_url: operation?.photo_url ?? "",
    };
  });
}

export async function downloadRowsAsXlsx(rows) {
  const XLSX = await import("xlsx");

  const ws = XLSX.utils.json_to_sheet(rows, {
    header: EXPORT_COLUMNS,
    cellDates: true,
  });

  ws["!cols"] = [
    { wch: 6 },
    { wch: 10 },
    { wch: 12 },
    { wch: 38 },
    { wch: 18 },
    { wch: 22 },
    { wch: 12 },
    { wch: 12 },
    { wch: 16 },
    { wch: 8 },
    { wch: 14 },
    { wch: 32 },
    { wch: 32 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "operations_filtrees");

  const wbout = XLSX.write(wb, {
    bookType: "xlsx",
    type: "array",
  });

  const blob = new Blob([wbout], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  downloadBlob(blob, `depenses_recettes_${todayStr()}.xlsx`);
}

export function downloadRowsAsCsv(rows) {
  const csvHeaders = EXPORT_COLUMNS.join(",");

  const toCsvValue = (value) => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10);
    }

    const text = String(value ?? "");
    return `"${text.replace(/"/g, '""')}"`;
  };

  const csvBody = safeArray(rows)
    .map((row) => EXPORT_COLUMNS.map((key) => toCsvValue(row[key])).join(","))
    .join("\n");

  const csv = `\ufeff${csvHeaders}\n${csvBody}\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });

  downloadBlob(blob, `depenses_recettes_${todayStr()}.csv`);
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
}

export function findCotisationPlan(planComptables) {
  return (
    safeArray(planComptables).find(
      (plan) =>
        /^70/i.test(plan?.code || "") && /coti/i.test(plan?.intitule || "")
    ) ||
    safeArray(planComptables).find((plan) => /^70/i.test(plan?.code || "")) ||
    safeArray(planComptables).find((plan) => plan?.sens === "RECETTE") ||
    null
  );
}

export function findLicensePlans(planComptables) {
  const plans = safeArray(planComptables);

  const recettePlan =
    plans.find(
      (plan) =>
        /^70/i.test(plan?.code || "") &&
        /(licen|assur)/i.test(plan?.intitule || "")
    ) ||
    plans.find((plan) => /^70/i.test(plan?.code || "")) ||
    plans.find((plan) => plan?.sens === "RECETTE") ||
    null;

  const depensePlan =
    plans.find(
      (plan) =>
        /^62/i.test(plan?.code || "") &&
        /(licen|assur|ffjda|france\s*judo)/i.test(plan?.intitule || "")
    ) ||
    plans.find((plan) => /^62/i.test(plan?.code || "")) ||
    plans.find((plan) => plan?.sens === "DEPENSE") ||
    null;

  return { recettePlan, depensePlan };
}

export function findPassSportPlans(planComptables) {
  const plans = safeArray(planComptables);

  const depensePlan =
    plans.find(
      (plan) =>
        /^6[5-7]/i.test(plan?.code || "") &&
        /(pass|réduc|reduc|aide)/i.test(plan?.intitule || "")
    ) ||
    plans.find((plan) => /^65|^67/.test(plan?.code || "")) ||
    plans.find((plan) => plan?.sens === "DEPENSE") ||
    null;

  const recettePlan =
    plans.find(
      (plan) =>
        /^74/i.test(plan?.code || "") ||
        /(subvention|etat|pass)/i.test(plan?.intitule || "")
    ) ||
    plans.find((plan) => plan?.sens === "RECETTE") ||
    null;

  return { depensePlan, recettePlan };
}

export function findReductionsPlan(planComptables) {
  const plans = safeArray(planComptables);

  return (
    plans.find(
      (plan) =>
        /^6[5-7]/i.test(plan?.code || "") &&
        /(réduc|reduc|remise|famille|saison)/i.test(plan?.intitule || "")
    ) ||
    plans.find((plan) => /^65|^67/.test(plan?.code || "")) ||
    plans.find((plan) => plan?.sens === "DEPENSE") ||
    null
  );
}

export function findBoutiquePlan(planComptables) {
  const plans = safeArray(planComptables);

  return (
    plans.find(
      (plan) =>
        /^707/i.test(plan?.code || "") ||
        /(marchandises|vente|boutique)/i.test(plan?.intitule || "")
    ) ||
    plans.find((plan) => /^70/i.test(plan?.code || "")) ||
    plans.find((plan) => plan?.sens === "RECETTE") ||
    null
  );
}